import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Clock, Calendar, Shield, Pencil, MapPin, Repeat } from "lucide-react";
import { toast } from "sonner";
import { Constants } from "@/integrations/supabase/types";

const QR_TYPES = [
  { value: "admin", label: "Administration (Attendance)", color: "text-blue-600" },
  { value: "academic", label: "Academic", color: "text-green-600" },
  { value: "examination", label: "Examination", color: "text-orange-600" },
  { value: "accounting", label: "Accounting", color: "text-purple-600" },
];

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const ALL_ROLES = Constants.public.Enums.app_role;
const CLASSES = ["CMT4", "CMT5", "CMT6", "PST4", "PST5", "PST6", "SW4", "SW5", "SW6"];

interface TimeWindow {
  start: string;
  end: string;
}

interface RuleForm {
  id?: string;
  qr_type: string;
  visible_to_roles: string[];
  day_of_week: string[];
  time_windows: TimeWindow[];
  date_from: string;
  date_to: string;
  is_active: boolean;
  geo_enabled: boolean;
  geo_latitude: string;
  geo_longitude: string;
  geo_radius_meters: string;
  target_classes: string[];
  is_repetitive: boolean;
}

const emptyRule = (): RuleForm => ({
  qr_type: "admin",
  visible_to_roles: [],
  day_of_week: [],
  time_windows: [],
  date_from: "",
  date_to: "",
  is_active: true,
  geo_enabled: false,
  geo_latitude: "",
  geo_longitude: "",
  geo_radius_meters: "100",
  target_classes: [],
  is_repetitive: false,
});

export default function QrVisibilityManager() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [editingRule, setEditingRule] = useState<RuleForm | null>(null);

  const { data: rules = [], isLoading } = useQuery({
    queryKey: ["qr-visibility-rules-admin"],
    queryFn: async () => {
      const { data } = await supabase
        .from("qr_visibility_rules")
        .select("*")
        .order("qr_type")
        .order("created_at", { ascending: false });
      return data || [];
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (rule: RuleForm) => {
      const payload: any = {
        qr_type: rule.qr_type,
        visible_to_roles: rule.visible_to_roles,
        day_of_week: rule.day_of_week,
        time_windows: rule.time_windows,
        date_from: rule.date_from || null,
        date_to: rule.date_to || null,
        is_active: rule.is_active,
        geo_enabled: rule.geo_enabled,
        geo_latitude: rule.geo_latitude ? parseFloat(rule.geo_latitude) : null,
        geo_longitude: rule.geo_longitude ? parseFloat(rule.geo_longitude) : null,
        geo_radius_meters: rule.geo_radius_meters ? parseInt(rule.geo_radius_meters) : 100,
        target_classes: rule.target_classes,
        created_by: user!.id,
      };
      if (rule.id) {
        const { error } = await supabase.from("qr_visibility_rules").update(payload).eq("id", rule.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("qr_visibility_rules").insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["qr-visibility-rules-admin"] });
      queryClient.invalidateQueries({ queryKey: ["qr-visibility-rules"] });
      setEditingRule(null);
      toast.success("Visibility rule saved");
    },
    onError: (err: any) => toast.error(err.message),
  });

  const suspendMutation = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await supabase.from("qr_visibility_rules").update({ is_active }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_, { is_active }) => {
      queryClient.invalidateQueries({ queryKey: ["qr-visibility-rules-admin"] });
      queryClient.invalidateQueries({ queryKey: ["qr-visibility-rules"] });
      toast.success(is_active ? "Rule resumed" : "Rule suspended");
    },
    onError: (err: any) => toast.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("qr_visibility_rules").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["qr-visibility-rules-admin"] });
      queryClient.invalidateQueries({ queryKey: ["qr-visibility-rules"] });
      toast.success("Rule deleted");
    },
    onError: (err: any) => toast.error(err.message),
  });

  const toggleDay = (day: string) => {
    if (!editingRule) return;
    const days = editingRule.day_of_week.includes(day)
      ? editingRule.day_of_week.filter(d => d !== day)
      : [...editingRule.day_of_week, day];
    setEditingRule({ ...editingRule, day_of_week: days });
  };

  const toggleRole = (role: string) => {
    if (!editingRule) return;
    const r = editingRule.visible_to_roles.includes(role)
      ? editingRule.visible_to_roles.filter(x => x !== role)
      : [...editingRule.visible_to_roles, role];
    setEditingRule({ ...editingRule, visible_to_roles: r });
  };

  const toggleClass = (cls: string) => {
    if (!editingRule) return;
    const c = editingRule.target_classes.includes(cls)
      ? editingRule.target_classes.filter(x => x !== cls)
      : [...editingRule.target_classes, cls];
    setEditingRule({ ...editingRule, target_classes: c });
  };

  const addTimeWindow = () => {
    if (!editingRule) return;
    setEditingRule({
      ...editingRule,
      time_windows: [...editingRule.time_windows, { start: "08:00", end: "16:00" }],
    });
  };

  const updateTimeWindow = (idx: number, field: "start" | "end", value: string) => {
    if (!editingRule) return;
    const windows = [...editingRule.time_windows];
    windows[idx] = { ...windows[idx], [field]: value };
    setEditingRule({ ...editingRule, time_windows: windows });
  };

  const removeTimeWindow = (idx: number) => {
    if (!editingRule) return;
    setEditingRule({
      ...editingRule,
      time_windows: editingRule.time_windows.filter((_, i) => i !== idx),
    });
  };

  const editRule = (rule: any) => {
    const dayOfWeek = rule.day_of_week || [];
    const dateFrom = rule.date_from || "";
    const isRepetitive = dayOfWeek.length > 0 && !!dateFrom;
    setEditingRule({
      id: rule.id,
      qr_type: rule.qr_type,
      visible_to_roles: rule.visible_to_roles || [],
      day_of_week: dayOfWeek,
      time_windows: Array.isArray(rule.time_windows) ? rule.time_windows as TimeWindow[] : [],
      date_from: dateFrom,
      date_to: rule.date_to || "",
      is_active: rule.is_active,
      geo_enabled: rule.geo_enabled || false,
      geo_latitude: rule.geo_latitude != null ? String(rule.geo_latitude) : "",
      geo_longitude: rule.geo_longitude != null ? String(rule.geo_longitude) : "",
      geo_radius_meters: rule.geo_radius_meters != null ? String(rule.geo_radius_meters) : "100",
      target_classes: rule.target_classes || [],
      is_repetitive: isRepetitive,
    });
  };

  const getQrLabel = (type: string) => QR_TYPES.find(q => q.value === type)?.label || type;
  const getQrColor = (type: string) => QR_TYPES.find(q => q.value === type)?.color || "";

  // Master per-type override toggles
  const { data: masterOverrides = [] } = useQuery({
    queryKey: ["qr-master-overrides-admin"],
    queryFn: async () => {
      const { data } = await supabase
        .from("system_settings")
        .select("id,key,value")
        .like("key", "qr_master_%");
      return data || [];
    },
  });

  const masterMap: Record<string, { id?: string; enabled: boolean | null }> = {};
  QR_TYPES.forEach(t => { masterMap[t.value] = { enabled: null }; });
  (masterOverrides as any[]).forEach(row => {
    const type = row.key.replace("qr_master_", "");
    const v = row.value;
    masterMap[type] = {
      id: row.id,
      enabled: typeof v === "object" && v !== null ? !!v.enabled : !!v,
    };
  });

  const masterToggleMutation = useMutation({
    mutationFn: async ({ qrType, enabled, existingId }: { qrType: string; enabled: boolean | null; existingId?: string }) => {
      const key = `qr_master_${qrType}`;
      // null = "use rules" → delete the override row
      if (enabled === null) {
        if (existingId) {
          const { error } = await supabase.from("system_settings").delete().eq("id", existingId);
          if (error) throw error;
        }
        return;
      }
      if (existingId) {
        const { error } = await supabase
          .from("system_settings")
          .update({ value: { enabled } as any, updated_by: user?.id })
          .eq("id", existingId);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("system_settings")
          .insert([{ key, value: { enabled } as any, updated_by: user?.id }]);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["qr-master-overrides-admin"] });
      queryClient.invalidateQueries({ queryKey: ["qr-master-overrides"] });
      toast.success("Master toggle updated");
    },
    onError: (err: any) => toast.error(err.message),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">QR Code Visibility Rules</h3>
          <p className="text-sm text-muted-foreground">Control when and to whom student QR codes are visible</p>
        </div>
        <Button onClick={() => setEditingRule(emptyRule())} size="sm">
          <Plus className="h-4 w-4 mr-1" /> Add Rule
        </Button>
      </div>

      {/* Master Override Toggles */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Master QR Switches</CardTitle>
          <p className="text-xs text-muted-foreground">
            Force ON or OFF per QR type. When set, this overrides all rules below. Set to "Use rules" to defer to the configured rules.
          </p>
        </CardHeader>
        <CardContent className="space-y-2">
          {QR_TYPES.map(t => {
            const current = masterMap[t.value];
            const state: "on" | "off" | "rules" =
              current.enabled === true ? "on" : current.enabled === false ? "off" : "rules";
            return (
              <div key={t.value} className="flex items-center justify-between gap-3 rounded-md border p-2">
                <div className={`text-sm font-medium ${t.color}`}>{t.label}</div>
                <div className="flex items-center gap-1">
                  <Button
                    size="sm"
                    variant={state === "on" ? "default" : "outline"}
                    onClick={() => masterToggleMutation.mutate({ qrType: t.value, enabled: true, existingId: current.id })}
                  >
                    On
                  </Button>
                  <Button
                    size="sm"
                    variant={state === "off" ? "destructive" : "outline"}
                    onClick={() => masterToggleMutation.mutate({ qrType: t.value, enabled: false, existingId: current.id })}
                  >
                    Off
                  </Button>
                  <Button
                    size="sm"
                    variant={state === "rules" ? "secondary" : "outline"}
                    onClick={() => masterToggleMutation.mutate({ qrType: t.value, enabled: null, existingId: current.id })}
                  >
                    Use rules
                  </Button>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Rule Editor */}
      {editingRule && (
        <Card className="border-primary/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{editingRule.id ? "Edit Rule" : "New Visibility Rule"}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>QR Code Type</Label>
                <Select value={editingRule.qr_type} onValueChange={v => setEditingRule({ ...editingRule, qr_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {QR_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-3 pt-6">
                <Switch checked={editingRule.is_active} onCheckedChange={v => setEditingRule({ ...editingRule, is_active: v })} />
                <Label>Active</Label>
              </div>
            </div>

            {/* Schedule Type */}
            <div className="space-y-3 rounded-lg border p-3">
              <div className="flex items-center gap-3">
                <Switch
                  checked={editingRule.is_repetitive}
                  onCheckedChange={v => setEditingRule({
                    ...editingRule,
                    is_repetitive: v,
                    day_of_week: v ? editingRule.day_of_week : [],
                  })}
                />
                <Label className="flex items-center gap-1.5">
                  <Repeat className="h-3.5 w-3.5" /> Repetitive (recurring weekly)
                </Label>
              </div>
              <p className="text-xs text-muted-foreground">
                {editingRule.is_repetitive
                  ? "Rule repeats on selected days within the date range"
                  : "Rule applies as a one-time date range"}
              </p>
            </div>

            {/* Days of week — only for repetitive */}
            {editingRule.is_repetitive && (
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> Repeat on Days</Label>
                <div className="flex flex-wrap gap-2">
                  {DAYS.map(day => (
                    <label key={day} className="flex items-center gap-1.5 text-sm cursor-pointer">
                      <Checkbox checked={editingRule.day_of_week.includes(day)} onCheckedChange={() => toggleDay(day)} />
                      <span className="capitalize">{day.slice(0, 3)}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Time Windows */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> Time Windows (EAT)</Label>
              {editingRule.time_windows.map((tw, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Input type="time" value={tw.start} onChange={e => updateTimeWindow(idx, "start", e.target.value)} className="w-32" />
                  <span className="text-sm text-muted-foreground">to</span>
                  <Input type="time" value={tw.end} onChange={e => updateTimeWindow(idx, "end", e.target.value)} className="w-32" />
                  <Button type="button" size="icon" variant="ghost" onClick={() => removeTimeWindow(idx)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={addTimeWindow}>
                <Plus className="h-3 w-3 mr-1" /> Add Time Window
              </Button>
              <p className="text-xs text-muted-foreground">Leave empty for all-day visibility</p>
            </div>

            {/* Date Range */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{editingRule.is_repetitive ? "Repeat From" : "Date From (optional)"}</Label>
                <Input type="date" value={editingRule.date_from} onChange={e => setEditingRule({ ...editingRule, date_from: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>{editingRule.is_repetitive ? "Repeat Until" : "Date To (optional)"}</Label>
                <Input type="date" value={editingRule.date_to} onChange={e => setEditingRule({ ...editingRule, date_to: e.target.value })} />
              </div>
            </div>

            {/* Target Classes */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5"><Shield className="h-3.5 w-3.5" /> Target Classes</Label>
              <div className="flex flex-wrap gap-2">
                {CLASSES.map(cls => (
                  <label key={cls} className="flex items-center gap-1.5 text-sm cursor-pointer">
                    <Checkbox checked={editingRule.target_classes.includes(cls)} onCheckedChange={() => toggleClass(cls)} />
                    <span>{cls}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">Leave empty to apply to all classes</p>
            </div>

            {/* Geofencing */}
            <div className="space-y-3 rounded-lg border p-3">
              <div className="flex items-center gap-3">
                <Switch checked={editingRule.geo_enabled} onCheckedChange={v => setEditingRule({ ...editingRule, geo_enabled: v })} />
                <Label className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> Geofencing</Label>
              </div>
              {editingRule.geo_enabled && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Latitude</Label>
                    <Input type="number" step="any" placeholder="-6.7924" value={editingRule.geo_latitude} onChange={e => setEditingRule({ ...editingRule, geo_latitude: e.target.value })} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Longitude</Label>
                    <Input type="number" step="any" placeholder="39.2083" value={editingRule.geo_longitude} onChange={e => setEditingRule({ ...editingRule, geo_longitude: e.target.value })} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Radius (meters)</Label>
                    <Input type="number" min="10" value={editingRule.geo_radius_meters} onChange={e => setEditingRule({ ...editingRule, geo_radius_meters: e.target.value })} />
                  </div>
                </div>
              )}
            </div>

            {/* Visible to Roles */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5"><Shield className="h-3.5 w-3.5" /> Visible to Roles (staff who can see this QR)</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                {ALL_ROLES.filter(r => r !== "student").map(role => (
                  <label key={role} className="flex items-center gap-1.5 text-xs cursor-pointer">
                    <Checkbox checked={editingRule.visible_to_roles.includes(role)} onCheckedChange={() => toggleRole(role)} />
                    <span>{role.replace(/_/g, " ")}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">Students always see their own QR codes (subject to time/geo rules)</p>
            </div>

            <div className="flex gap-2 pt-2">
              <Button onClick={() => saveMutation.mutate(editingRule)} disabled={saveMutation.isPending}>
                Save Rule
              </Button>
              <Button variant="outline" onClick={() => setEditingRule(null)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Existing Rules */}
      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading rules...</p>
      ) : rules.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground text-sm">
            No visibility rules configured. QR codes will be hidden by default.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {rules.map((rule: any) => (
            <Card key={rule.id} className={!rule.is_active ? "opacity-60" : ""}>
              <CardContent className="py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-1 flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`font-semibold text-sm ${getQrColor(rule.qr_type)}`}>
                        {getQrLabel(rule.qr_type)}
                      </span>
                      <Badge variant={rule.is_active ? "default" : "destructive"} className="text-[10px]">
                        {rule.is_active ? "Active" : "Suspended"}
                      </Badge>
                      {rule.day_of_week?.length > 0 && rule.date_from && (
                        <Badge variant="outline" className="text-[10px] gap-0.5">
                          <Repeat className="h-2.5 w-2.5" /> Repetitive
                        </Badge>
                      )}
                      {rule.geo_enabled && (
                        <Badge variant="outline" className="text-[10px] gap-0.5">
                          <MapPin className="h-2.5 w-2.5" /> Geo
                        </Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                      {rule.day_of_week?.length > 0 && (
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {(rule.day_of_week as string[]).map((d: string) => d.slice(0, 3).toUpperCase()).join(", ")}
                        </span>
                      )}
                      {Array.isArray(rule.time_windows) && rule.time_windows.length > 0 && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {(rule.time_windows as any[]).map((tw: any) => `${tw.start}–${tw.end}`).join(", ")}
                        </span>
                      )}
                      {rule.date_from && <span>From: {rule.date_from}</span>}
                      {rule.date_to && <span>To: {rule.date_to}</span>}
                      {rule.geo_enabled && rule.geo_latitude != null && (
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {rule.geo_latitude.toFixed(4)}, {rule.geo_longitude?.toFixed(4)} ({rule.geo_radius_meters || 100}m)
                        </span>
                      )}
                    </div>
                    {rule.target_classes?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {(rule.target_classes as string[]).map((c: string) => (
                          <Badge key={c} variant="outline" className="text-[10px] px-1.5 py-0 bg-muted/50">
                            {c}
                          </Badge>
                        ))}
                      </div>
                    )}
                    {rule.visible_to_roles?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {(rule.visible_to_roles as string[]).map((r: string) => (
                          <Badge key={r} variant="outline" className="text-[10px] px-1.5 py-0">
                            {r.replace(/_/g, " ")}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 px-2 text-[11px]"
                      onClick={() => suspendMutation.mutate({ id: rule.id, is_active: !rule.is_active })}
                    >
                      {rule.is_active ? "Suspend" : "Resume"}
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => editRule(rule)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive"
                      onClick={() => { if (confirm("Delete this rule?")) deleteMutation.mutate(rule.id); }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
